// LayerPanel.jsx
// Sidebar panel listing map layers with visibility and ownership-gated rename/delete.
//
// Improvements over v1:
//   - Click row to set active layer (accent left border + bold label)
//   - Shape count badge per layer
//   - Delete confirmation dialog for layers that have shapes
//   - ticket 01 (layer-panel-rework): ownership display + gated rename/delete via a shared
//     3-dot menu (RowActionsMenu)
//   - ticket 02 (layer-panel-rework): per-layer color/opacity removed (mil-sym-ts-web owns
//     symbol coloring via SIDC/affiliation)
//   - ticket 03 (layer-panel-rework): auto-derived DTG start/end range per layer

import React, {useRef, useState} from 'react';
import {
    Box, Stack, Typography, IconButton, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
    TextField, Chip,
} from '@mui/material';
import VisibilityRounded from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRounded from '@mui/icons-material/VisibilityOffRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import DragIndicatorRounded from '@mui/icons-material/DragIndicatorRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import LockRounded from '@mui/icons-material/LockRounded';
import LockOpenRounded from '@mui/icons-material/LockOpenRounded';
import RowActionsMenu from './RowActionsMenu';
import {formatDtg} from '../map/editor/MapTimeline';

// ── Delete confirmation dialog ───────────────────────────────────────────────

function DeleteLayerDialog({open, label, shapeCount, onConfirm, onCancel}) {
    return (
        <Dialog open={open} onClose={onCancel} maxWidth="xs">
            <DialogTitle sx={{fontSize: 15}}>Delete layer?</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{fontSize: 13}}>
                    {shapeCount > 0
                        ? `"${label}" contains ${shapeCount} shape${shapeCount !== 1 ? 's' : ''}. Deleting it will permanently remove all shapes on this layer.`
                        : `Delete layer "${label}"?`}
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{px: 2, pb: 1.5}}>
                <Button size="small" onClick={onCancel}>Cancel</Button>
                <Button size="small" color="error" variant="contained" onClick={onConfirm}>Delete</Button>
            </DialogActions>
        </Dialog>
    );
}

// ── LayerPanel ───────────────────────────────────────────────────────────────

// rerender-memo: memoized so it skips re-render when layers/callbacks are unchanged — mirrors
// the pattern already used on ContextPanel/ShapeForm/ViewsPanel.
const LayerPanel = React.memo(function LayerPanel({
    layers,
    activeLayerId,
    outOfViewLayerIds = null,
    hasLayersInDocument = true,
    layerShapeCounts = {},
    layerDtgRanges = {},
    currentUserId = null,
    onUpdateLayer,
    onDeleteLayer,
    onReorderLayer,
    onSetActiveLayer,
    onAddLayer,
    isReadOnly = false,
}) {
    const [pendingDelete, setPendingDelete] = useState(null); // {layerId, label, count}
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState('');
    // ticket 09 (layer-panel-audit): tracks which row is mid-drag so the row can expose its
    // current aria-grabbed state to screen readers, mirroring native HTML5 DnD visually.
    const [draggedIndex, setDraggedIndex] = useState(null);
    // ticket 07: tracks whether the current edit session already submitted an update (via
    // Enter) — unmounting the focused TextField after Enter can fire a native blur before the
    // component fully unmounts, re-invoking onBlur's own onUpdateLayer call with the same value.
    // Reset to false whenever a new edit session opens; set true right when Enter submits.
    const editSubmittedRef = useRef(false);

    // ticket 06 (extend-out-of-view-indicator): accept a Set or plain array — `Array.isArray`
    // check lets callers pass either without needing to know which one LayerPanel prefers.
    const outOfViewIdSet = outOfViewLayerIds instanceof Set
        ? outOfViewLayerIds
        : new Set(Array.isArray(outOfViewLayerIds) ? outOfViewLayerIds : []);

    const handleDragStart = (e, index) => {
        if (isReadOnly) { e.preventDefault(); return; }
        e.dataTransfer.setData('text/plain', String(index));
        setDraggedIndex(index);
    };
    const handleDragEnd = () => setDraggedIndex(null);
    const handleDrop = (e, targetIndex) => {
        e.preventDefault();
        const sourceIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
        if (sourceIndex === targetIndex || isNaN(sourceIndex)) return;
        onReorderLayer?.(sourceIndex, targetIndex);
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    // ticket 09 (layer-panel-audit): keyboard-only equivalents of click-to-activate and
    // drag-to-reorder. Only handled when the keydown originates directly on the row itself
    // (e.target === e.currentTarget) — nested controls (icon buttons, the rename TextField)
    // already have their own key handling and shouldn't also trigger the row's.
    const handleRowKeyDown = (e, index, layerId) => {
        if (isReadOnly) return;
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSetActiveLayer?.(layerId);
            return;
        }
        if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
            e.preventDefault();
            const targetIndex = e.key === 'ArrowUp' ? index - 1 : index + 1;
            if (targetIndex < 0 || targetIndex > layers.length - 1) return;
            onReorderLayer?.(index, targetIndex);
        }
    };

    const requestDelete = (layerId, label, count) => {
        if (isReadOnly) return;
        if (count > 0) {
            setPendingDelete({layerId, label, count});
        } else {
            onDeleteLayer?.(layerId);
        }
    };

    const confirmDelete = () => {
        if (pendingDelete) onDeleteLayer?.(pendingDelete.layerId);
        setPendingDelete(null);
    };

    return (
        <Stack>
            <Box>
                {(!layers || layers.length === 0) ? (
                    <Typography
                        variant="body2"
                        sx={{p: 1.5, color: 'text.disabled', fontSize: 12, lineHeight: 1.5}}
                    >
                        {hasLayersInDocument
                            // Ticket 06: the map has layers, but none currently qualify (viewport
                            // panned away from all their markings, and/or the DTG cursor is
                            // scrubbed to a time before/after they exist) — distinct message from
                            // the true-empty-document case below so it's clear layers do exist.
                            ? 'No layers visible in this area/time. Pan the map or move the DTG cursor.'
                            : 'No layers yet. Use the drawing tools to add features.'}
                    </Typography>
                ) : (
                    <Stack component="ul" sx={{m: 0, p: 0, listStyle: 'none'}}>
                        {layers.map((layer, i) => {
                            const props = layer.properties || {};
                            const layerId = String(layer.id);
                            const label = props.label || props.name || `Layer ${i + 1}`;
                            const ownerId = props.ownerId || null;
                            // ticket 01: unowned (legacy, pre-ownership) layers behave as "owned by
                            // everyone" — same as today's pre-ownership rename/delete access.
                            const isOwner = !ownerId || ownerId === currentUserId;
                            const visible = props.visible !== false;
                            const isActive = layerId === activeLayerId;
                            // ticket 06: out-of-view is now a set-membership check, independent of
                            // active status — every filtered-out layer gets the visual treatment
                            // below, not just the active/drawing-target one.
                            const isOutOfView = outOfViewIdSet.has(layerId);
                            const shapeCount = layerShapeCounts[layerId] ?? 0;
                            const status = props.status || 'working';
                            const isApproved = status === 'approved';
                            // ticket 03: auto-derived from the layer's markings' snapshot history —
                            // "—" when the layer has no markings/snapshots at all.
                            const dtgRange = layerDtgRanges[layerId];
                            const dtgText = dtgRange && dtgRange.start != null && dtgRange.end != null
                                ? `DTG ${formatDtg(dtgRange.start)} – ${formatDtg(dtgRange.end)}`
                                : '—';

                            return (
                                <Stack
                                    key={layerId}
                                    component="li"
                                    draggable={!isReadOnly}
                                    onDragStart={(e) => handleDragStart(e, i)}
                                    onDragEnd={handleDragEnd}
                                    onDrop={(e) => handleDrop(e, i)}
                                    onDragOver={handleDragOver}
                                    onClick={() => !isReadOnly && onSetActiveLayer?.(layerId)}
                                    onKeyDown={(e) => handleRowKeyDown(e, i, layerId)}
                                    role={isReadOnly ? undefined : 'button'}
                                    tabIndex={isReadOnly ? undefined : 0}
                                    aria-grabbed={isReadOnly ? undefined : draggedIndex === i}
                                    aria-dropeffect={isReadOnly ? undefined : 'move'}
                                    sx={(theme) => ({
                                        pl: 0,
                                        pr: 1,
                                        py: 0.75,
                                        gap: 0.5,
                                        cursor: isReadOnly ? 'default' : 'pointer',
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                        // ticket 06: non-active out-of-view rows get a dashed
                                        // neutral (divider-colored) left border so they read as
                                        // "flagged" without competing with the active row's accent
                                        // border below.
                                        borderLeft: isActive
                                            ? `3px ${isOutOfView ? 'dashed' : 'solid'} ${theme.palette.primary.main}`
                                            : (isOutOfView ? `3px dashed ${theme.palette.divider}` : '3px solid transparent'),
                                        bgcolor: isActive ? 'action.selected' : 'transparent',
                                        opacity: isOutOfView ? 0.65 : (isApproved ? 0.85 : 1),
                                        '&:hover': {bgcolor: isActive ? 'action.selected' : 'action.hover'},
                                        transition: 'background-color 0.1s',
                                    })}
                                >
                                    <Stack direction="row" alignItems="center" gap={0.5}>
                                        <Box sx={{color: 'text.disabled', display: 'flex', alignItems: 'center', pl: 0.5}}>
                                            <DragIndicatorRounded
                                                aria-label={isReadOnly ? undefined : 'Drag to reorder layer'}
                                                sx={{fontSize: 16, opacity: isReadOnly ? 0.3 : 0.6}}
                                            />
                                        </Box>

                                        {editingId === layerId ? (
                                            <TextField
                                                size="small"
                                                value={editName}
                                                autoFocus
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) => setEditName(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        editSubmittedRef.current = true;
                                                        onUpdateLayer?.(layerId, {label: editName.trim() || label});
                                                        setEditingId(null);
                                                    } else if (e.key === 'Escape') {
                                                        editSubmittedRef.current = true;
                                                        setEditingId(null);
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (editSubmittedRef.current) return;
                                                    editSubmittedRef.current = true;
                                                    onUpdateLayer?.(layerId, {label: editName.trim() || label});
                                                    setEditingId(null);
                                                }}
                                                inputProps={{style: {fontSize: 12, padding: '2px 6px'}}}
                                                sx={{flex: 1, minWidth: 0}}
                                            />
                                        ) : (
                                            <Tooltip
                                                title={isOutOfView
                                                    ? (isActive
                                                        ? 'Drawing target — not visible in the current view/time'
                                                        : 'Not visible in the current view/time')
                                                    : label}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        flex: 1,
                                                        fontSize: 13,
                                                        fontWeight: isActive ? 600 : 400,
                                                        color: isActive ? 'text.primary' : 'text.secondary',
                                                        fontStyle: isOutOfView ? 'italic' : 'normal',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {label}
                                                    {isOutOfView && '  (out of view)'}
                                                </Typography>
                                            </Tooltip>
                                        )}

                                        {/* Shape count badge */}
                                        {shapeCount > 0 && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontSize: 10,
                                                    color: 'text.disabled',
                                                    minWidth: 16,
                                                    textAlign: 'right',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                {shapeCount}
                                            </Typography>
                                        )}

                                        {/* Status chip */}
                                        <Chip
                                            label={isApproved ? 'Approved' : 'Working'}
                                            size="small"
                                            sx={{
                                                height: 14,
                                                fontSize: 9,
                                                fontWeight: 700,
                                                letterSpacing: 0.3,
                                                px: 0.25,
                                                flexShrink: 0,
                                                bgcolor: isApproved ? 'info.50' : 'warning.50',
                                                color: isApproved ? 'info.dark' : 'warning.dark',
                                                border: isApproved ? '1px solid' : '1px solid',
                                                borderColor: isApproved ? 'info.200' : 'warning.200',
                                                '& .MuiChip-label': {px: 0.5},
                                            }}
                                        />

                                        <Box onClick={(e) => e.stopPropagation()} sx={{display: 'flex'}}>
                                            <Tooltip title={visible ? 'Hide layer' : 'Show layer'}>
                                                <IconButton
                                                    size="small"
                                                    sx={{p: 0.25}}
                                                    onClick={() => !isReadOnly && onUpdateLayer?.(layerId, {visible: !visible})}
                                                    disabled={isReadOnly}
                                                    aria-label={visible ? 'Hide layer' : 'Show layer'}
                                                >
                                                    {visible
                                                        ? <VisibilityRounded sx={{fontSize: 16}}/>
                                                        : <VisibilityOffRounded sx={{fontSize: 16}}/>}
                                                </IconButton>
                                            </Tooltip>

                                            {/* Working / Approved status toggle — ticket 03: gated
                                                behind isOwner, same rule as Rename/Delete below
                                                (unowned/legacy layers count as owned by everyone). */}
                                            {!isReadOnly && isOwner && (
                                                <Tooltip title={isApproved ? 'Mark as Working' : 'Mark as Approved'}>
                                                    <IconButton
                                                        size="small"
                                                        sx={{
                                                            p: 0.25,
                                                            color: isApproved ? 'info.main' : 'warning.main',
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onUpdateLayer?.(layerId, {status: isApproved ? 'working' : 'approved'});
                                                        }}
                                                        aria-label={isApproved ? 'Mark as Working' : 'Mark as Approved'}
                                                    >
                                                        {isApproved
                                                            ? <LockRounded sx={{fontSize: 16}}/>
                                                            : <LockOpenRounded sx={{fontSize: 16}}/>}
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {/* ticket 01: shared 3-dot menu, owner-gated — renders
                                                nothing at all (no disabled button) for a layer owned
                                                by someone else. Unowned (legacy) layers count as
                                                owned by everyone. */}
                                            {!isReadOnly && isOwner && (
                                                <RowActionsMenu
                                                    ariaLabel="Layer actions"
                                                    actions={[
                                                        {
                                                            label: 'Rename',
                                                            icon: <EditRounded fontSize="inherit"/>,
                                                            onClick: () => {
                                                                editSubmittedRef.current = false;
                                                                setEditingId(layerId);
                                                                setEditName(label);
                                                            },
                                                        },
                                                        {
                                                            label: 'Delete',
                                                            icon: <DeleteRounded fontSize="inherit"/>,
                                                            color: 'error.main',
                                                            onClick: () => requestDelete(layerId, label, shapeCount),
                                                        },
                                                    ]}
                                                />
                                            )}
                                        </Box>
                                    </Stack>

                                    {/* ticket 01/03: raw owner id (matching how marking.author is
                                        shown elsewhere — no name-resolution lookup) and the layer's
                                        auto-derived DTG range. */}
                                    <Stack direction="row" alignItems="center" gap={1} sx={{px: 3}}>
                                        {ownerId && (
                                            <Typography variant="caption" sx={{fontSize: 10, color: 'text.disabled'}}>
                                                Owner: {ownerId}
                                            </Typography>
                                        )}
                                        <Typography variant="caption" sx={{fontSize: 10, color: 'text.disabled'}}>
                                            {dtgText}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            );
                        })}
                    </Stack>
                )}
            </Box>

            {/* Add layer button */}
            {!isReadOnly && onAddLayer && (
                <Box sx={(theme) => ({px: 1.5, py: 0.75, borderTop: `1px solid ${theme.palette.divider}`})}>
                    <Button
                        size="small"
                        variant="text"
                        startIcon={<AddRounded sx={{fontSize: 14}}/>}
                        onClick={onAddLayer}
                        sx={{fontSize: 11, textTransform: 'none', color: 'text.secondary', px: 0.5}}
                    >
                        Add layer
                    </Button>
                </Box>
            )}

            <DeleteLayerDialog
                open={Boolean(pendingDelete)}
                label={pendingDelete?.label ?? ''}
                shapeCount={pendingDelete?.count ?? 0}
                onConfirm={confirmDelete}
                onCancel={() => setPendingDelete(null)}
            />
        </Stack>
    );
});

export default LayerPanel;
